import defaultFetch from 'isomorphic-unfetch';
import { stringify } from 'query-string';
import { partialRight } from 'ramda';
import type {
  CampaignDetailsApiClient,
  CampaignDetailsQueryParams,
  CampaignDetailsResponse,
} from '@/scopes/search/search/domain/campaign-details/types';

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string;
  };
  fetch: typeof defaultFetch;
  queryStringSerializer: {
    serialize: typeof stringify;
  };
}

export const createCampaignDetailsApiClient = (
  { contextItemId }: { contextItemId: string },
  dependencies: Partial<Dependencies> = {}
): CampaignDetailsApiClient => {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = { serialize: partialRight(stringify, [{ arrayFormat: 'index' }]) },
  } = dependencies as Dependencies;

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  };

  async function getCampaignDetails(campaignDetailsQueryParams: CampaignDetailsQueryParams) {
    const url = buildAPIUrl(campaignDetailsQueryParams);

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error('Failed to fetch campaign details');
    }

    const data = (await response.json()) as CampaignDetailsResponse;
    return data;
  }

  function buildAPIUrl({ campaignId }: CampaignDetailsQueryParams) {
    const queryString = queryStringSerializer.serialize({ campaignId, contextItemId });

    return `${env.NEXT_PUBLIC_API_BASE_URL}/CampaignsApi/GetCampaignDetails?${queryString}`;
  }

  return { getCampaignDetails };
};
